import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, Heart, Share2, FileText, Bell, MapPin,
  TrendingUp, Shield, Building2, Award, ArrowRight,
  Sparkles, MessageSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated counter component
function AnimatedCounter({ value, duration = 2000 }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (value === 0) return;
    
    const step = Math.ceil(value / (duration / 50));
    let current = 0;
    
    const timer = setInterval(() => {
      current += step;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(current);
      }
    }, 50);

    return () => clearInterval(timer);
  }, [value, duration]);

  return <>{count.toLocaleString()}</>;
}

export default function ImpactDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/impact-stats`);
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching impact stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-safety-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  if (!stats) return null;

  // Format large numbers (e.g., 4500000 -> "4.5M")
  const formatLargeNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toLocaleString();
  };

  // PRIMARY: Platform Scale & Infrastructure
  const scaleStats = [
    {
      icon: Users,
      value: stats.total_cases,
      displayValue: formatLargeNumber(stats.total_cases),
      label: 'Cases Indexed',
      description: 'From NamUs, NCMEC, Charley Project & more',
      color: 'text-safety-orange-500',
      bgColor: 'bg-safety-orange-100 dark:bg-safety-orange-900/30',
    },
    {
      icon: Sparkles,
      value: stats.connections_analyzed || 4500000,
      displayValue: formatLargeNumber(stats.connections_analyzed || 4500000),
      label: 'Connections Analyzed',
      description: 'Cross-case similarity comparisons',
      color: 'text-purple-500',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      icon: MessageSquare,
      value: stats.tips_submitted,
      displayValue: stats.tips_submitted?.toLocaleString() || '0',
      label: 'Community Tips',
      description: 'Anonymous tips from the public',
      color: 'text-trust-blue-500',
      bgColor: 'bg-trust-blue-100 dark:bg-trust-blue-900/30',
    },
    {
      icon: MapPin,
      value: stats.states_covered || 50,
      displayValue: stats.states_covered?.toString() || '50',
      label: 'States & Territories',
      description: 'Nationwide coverage',
      color: 'text-green-500',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
    },
  ];

  // SECONDARY: Progress & Activity
  const progressStats = [
    { 
      icon: TrendingUp, 
      value: stats.active_matches_reviewing || 0, 
      label: 'Active Matches', 
      description: 'Under investigative review' 
    },
    { 
      icon: Shield, 
      value: stats.duplicates_consolidated || 32000, 
      label: 'Duplicates Consolidated', 
      description: 'Records merged for accuracy' 
    },
    { 
      icon: FileText, 
      value: stats.canonical_identities || 15000, 
      label: 'Unified Identities', 
      description: 'Cross-source case profiles' 
    },
    { 
      icon: Bell, 
      value: stats.potential_leads_flagged || 0, 
      label: 'Potential Leads', 
      description: 'High-confidence matches' 
    },
  ];

  // TERTIARY: Confirmed Outcomes (moved lower)
  const outcomeStats = {
    reunifications: stats.confirmed_reunifications || stats.resolved_reunited || 0,
    successStories: stats.success_stories || 0,
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <Badge className="mb-4 bg-trust-blue-100 text-trust-blue-700 dark:bg-trust-blue-900/30 dark:text-trust-blue-400">
            <TrendingUp className="w-3 h-3 mr-1" />
            Platform Scale
          </Badge>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
            Building the Infrastructure for Reunification
          </h2>
          <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            A growing investigative engine connecting cases, analyzing patterns, and empowering communities.
          </p>
        </motion.div>

        {/* PRIMARY: Scale & Infrastructure Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {scaleStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="relative overflow-hidden hover:shadow-lg transition-shadow h-full">
                <CardContent className="p-4 lg:p-6">
                  <div className={`w-10 h-10 lg:w-12 lg:h-12 ${stat.bgColor} rounded-xl flex items-center justify-center mb-3`}>
                    <stat.icon className={`w-5 h-5 lg:w-6 lg:h-6 ${stat.color}`} />
                  </div>
                  <div className={`text-2xl lg:text-3xl font-bold ${stat.color} mb-1`}>
                    {stat.displayValue}+
                  </div>
                  <div className="font-semibold text-foreground text-sm lg:text-base mb-1">{stat.label}</div>
                  <div className="text-xs text-muted-foreground hidden sm:block">{stat.description}</div>
                </CardContent>
                <div className={`absolute -right-4 -bottom-4 w-20 h-20 ${stat.bgColor} rounded-full opacity-30`} />
              </Card>
            </motion.div>
          ))}
        </div>

        {/* SECONDARY: Progress & Activity Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-8"
        >
          <Card className="bg-slate-900 dark:bg-slate-800 text-white">
            <CardContent className="p-4 lg:p-6">
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-4 text-center">
                Ongoing Progress
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                {progressStats.map((stat, index) => (
                  <div key={stat.label} className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <stat.icon className="w-4 h-4 text-slate-400" />
                      <span className="text-xl lg:text-2xl font-bold text-white">
                        <AnimatedCounter value={stat.value} duration={1500} />
                      </span>
                    </div>
                    <div className="text-xs lg:text-sm text-slate-300 font-medium">{stat.label}</div>
                    <div className="text-xs text-slate-500 hidden lg:block">{stat.description}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* TERTIARY: Confirmed Outcomes (smaller, transparent positioning) */}
        {(outcomeStats.reunifications > 0 || outcomeStats.successStories > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-8"
          >
            <Card className="border-green-200 dark:border-green-900 bg-green-50/50 dark:bg-green-900/10">
              <CardContent className="p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <Heart className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {outcomeStats.reunifications}
                      </div>
                      <div className="text-xs text-green-700 dark:text-green-500">Confirmed Reunifications</div>
                    </div>
                  </div>
                  <div className="hidden sm:block w-px h-12 bg-green-200 dark:bg-green-800" />
                  <div className="text-center sm:text-left max-w-md">
                    <p className="text-sm text-green-800 dark:text-green-300">
                      Verified cases where LinkTheMissing contributed investigative value to families and law enforcement.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 text-center"
        >
          <p className="text-muted-foreground mb-6">
            Join the growing community helping to connect missing persons with their families.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-safety-orange-500 hover:bg-safety-orange-600 rounded-full gap-2">
              <Link to="/browse">
                <Sparkles className="w-4 h-4" />
                Explore Cases
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full gap-2">
              <Link to="/impact-stories">
                <Heart className="w-4 h-4" />
                Read Impact Stories
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
